import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FaExclamationTriangle, FaTrashAlt } from 'react-icons/fa';
import { commercialConfigAPI } from '../services/api';
import { Modal, ModalFooter } from './Modals';
import PasswordInput from './PasswordInput';

// Commercial Setup → Danger zone. Clears all master + transaction data so the
// installation can be handed to a new client (e.g. test data before go-live).
// A full signed backup of everything (incl. audit trail) is taken first.
const HardResetCard = () => {
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ reason: '', confirm_phrase: '', password: '', clear_files: true });
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const loadPreview = useCallback(async () => {
    try {
      const res = await commercialConfigAPI.hardResetPreview();
      setPreview(res.data.data);
      setJob(res.data.data.job);
    } catch {
      setPreview(null);
    }
  }, []);

  useEffect(() => { loadPreview(); return () => clearInterval(pollRef.current); }, [loadPreview]);

  const poll = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const state = (await commercialConfigAPI.hardResetStatus()).data.data;
        setJob(state);
        if (!state.current) {
          clearInterval(pollRef.current);
          if (state.last?.status === 'COMPLETED') {
            toast.success('Hard reset completed. The app now holds no client data.');
            loadPreview();
          } else if (state.last?.status === 'FAILED') {
            toast.error(`Hard reset failed: ${state.last.error}`);
          }
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await commercialConfigAPI.hardReset(form);
      setOpen(false);
      setForm({ reason: '', confirm_phrase: '', password: '', clear_files: true });
      toast.info('Hard reset started. A full archive backup is being taken first.');
      setJob({ current: { phase: 'Taking full backup (archive)' } });
      poll();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Hard reset could not start');
    } finally {
      setSubmitting(false);
    }
  };

  if (!preview) return null;
  const totalRows = preview.wipe.reduce((sum, t) => sum + t.approx_rows, 0);
  const running = !!job?.current;
  const canSubmit = form.confirm_phrase.trim() === preview.confirm_phrase && form.reason.trim().length >= 10 && form.password;

  return (
    <div className="card" style={{ marginTop: '20px', border: '1px solid var(--danger-border, #fca5a5)' }}>
      <div className="card-header">
        <h3 className="card-title" style={{ color: 'var(--danger-text, #b91c1c)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FaExclamationTriangle /> Danger zone: Hard reset for a new client
        </h3>
      </div>
      <div className="card-body">
        <p style={{ marginTop: 0 }}>
          Removes <strong>all master and transaction data</strong> (APIs, products, equipment, trains, units and sections, cleaning config, plans, MACO, results, VRL, CCV, training, documents, e-signatures, the audit trail and uploaded files) so this installation can be handed over clean, e.g. after testing.
        </p>
        <p>
          <strong>Kept:</strong> user logins, roles and permissions, departments, license and commercial plan, system settings, approval chains, alert channels and templates, and AI providers. Document number counters restart from 1.
        </p>
        <p className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
          A full signed backup (database, audit trail and files) is taken first and kept in Backup &amp; Restore as type PRE_RESET. If that backup fails, nothing is deleted. The reset is recorded as the first entry of the new audit trail.
        </p>
        {running && (
          <div style={{ padding: '10px 12px', borderRadius: '6px', background: 'var(--warning-bg, #fffbeb)', border: '1px solid var(--warning-border, #f59e0b)', marginBottom: '10px' }}>
            Reset running: {job.current.phase || 'working…'}. Do not close the server.
          </div>
        )}
        {!running && job?.last && (
          <p style={{ fontSize: 'var(--fs-sm)' }}>
            Last reset: <strong>{job.last.status}</strong> on {new Date(job.last.finishedAt).toLocaleString()} by {job.last.by}
            {job.last.backupId ? ` · archive backup ${job.last.backupId}` : ''}{job.last.error ? ` · ${job.last.error}` : ''}
          </p>
        )}
        <button className="btn btn-danger" disabled={running} onClick={() => setOpen(true)}>
          <FaTrashAlt /> Hard reset… ({totalRows.toLocaleString()} rows in {preview.wipe.filter((t) => t.approx_rows > 0).length} tables)
        </button>
      </div>

      {open && (
        <Modal isOpen size="md" title="Hard reset: remove all client data" onClose={() => setOpen(false)}>
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ padding: '10px 12px', borderRadius: '6px', background: 'var(--danger-bg, #fef2f2)', border: '1px solid var(--danger-border, #fca5a5)', color: 'var(--danger-text, #b91c1c)' }}>
              This permanently clears {totalRows.toLocaleString()} rows. Only the archive backup taken in the first step can bring them back.
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Reason *</label>
              <textarea className="form-control" rows={2} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="e.g. Removing UAT test data before go-live for <client>" />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="checkbox" checked={form.clear_files} onChange={(e) => setForm((f) => ({ ...f, clear_files: e.target.checked }))} />
              Also delete uploaded files (SOPs, attachments, documents)
            </label>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Type <code>{preview.confirm_phrase}</code> to confirm *</label>
              <input className="form-control" value={form.confirm_phrase} onChange={(e) => setForm((f) => ({ ...f, confirm_phrase: e.target.value }))} autoComplete="off" />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Your password (e-signature) *</label>
              <PasswordInput value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} autoComplete="current-password" />
            </div>
          </div>
          <ModalFooter>
            <button className="btn btn-outline" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn btn-danger" disabled={!canSubmit || submitting} onClick={submit}>
              <FaTrashAlt /> {submitting ? 'Starting…' : 'Archive and reset'}
            </button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

export default HardResetCard;
